import { z } from 'zod';
// Money is always carried as integer cents. Floating-point arithmetic on
// monetary values produces real financial bugs — never store an amount as a
// float, never multiply or divide an amount with a non-integer factor. Locale
// formatting happens only at the UI boundary.
//
// Currency travels with the amount because the system supports EUR today but
// is not architecturally locked to it. A bare amount is meaningless without
// its currency.
export const MoneySchema = z.object({
    amount: z.number().int().nonnegative(),
    currency: z.literal('EUR'),
});
// Integer cents → a plain decimal string with exactly two places ("3500" →
// "35.00"), for machine-readable outputs (schema.org offers, JSON feeds) that
// want a number without locale formatting. Integer arithmetic only.
export const centsToDecimalString = (cents) => {
    if (!Number.isSafeInteger(cents) || cents < 0) {
        throw new RangeError(`centsToDecimalString expects non-negative integer cents, got ${String(cents)}`);
    }
    const euros = Math.trunc(cents / 100);
    const rest = cents % 100;
    return `${String(euros)}.${String(rest).padStart(2, '0')}`;
};
// THE money formatter for every customer surface — widget, hosted page and
// its Markdown/feed twins, emails. Integer cents in, always two decimals,
// thousands grouped: "2.134,00 €" in Spanish, "€2,134.00" in English
// (handover J). Built by hand because es-ES Intl leaves four-digit amounts
// ungrouped ("2134,00 €") and `useGrouping: 'always'` is not available in
// every browser the widget runs in. The space before the euro sign is
// non-breaking so an amount never wraps; a negative amount takes the true
// minus sign (U+2212): "−30,00 €", "−€30.00". Machine-readable outputs
// (JSON-LD offers) use `centsToDecimalString` instead. The ESLint rule in
// eslint.config.js bans any other currency formatting on those surfaces.
const NBSP = '\u00a0';
const MINUS = '\u2212';
const groupThousands = (digits, separator) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
export const formatAmount = (cents, language) => {
    if (!Number.isSafeInteger(cents)) {
        // A non-integer cent value means an upstream float bug: fail loudly rather
        // than silently rounding money.
        throw new TypeError(`formatAmount expects integer cents, got ${String(cents)}`);
    }
    const sign = cents < 0 ? MINUS : '';
    const absolute = Math.abs(cents);
    const whole = String(Math.trunc(absolute / 100));
    const fraction = String(absolute % 100).padStart(2, '0');
    return language === 'es'
        ? `${sign}${groupThousands(whole, '.')},${fraction}${NBSP}€`
        : `${sign}€${groupThousands(whole, ',')}.${fraction}`;
};
// The SHORT money string — whole euros, thousands grouped: "1.234 €" /
// "€1,234". Only for chart axes and bar value labels and the places that have
// always shown whole euros (prices that never carry cents); everything else
// uses `formatAmount` (Cálido rule, R-5). Cents are rounded half away from
// zero to the nearest euro, in integer arithmetic: 123450 → "1.235 €",
// −250 → "−3 €".
export const formatAmountShort = (cents, language) => {
    if (!Number.isSafeInteger(cents)) {
        throw new TypeError(`formatAmountShort expects integer cents, got ${String(cents)}`);
    }
    const absolute = Math.abs(cents);
    const euros = Math.trunc(absolute / 100) + (absolute % 100 >= 50 ? 1 : 0);
    const sign = cents < 0 && euros > 0 ? MINUS : '';
    const whole = String(euros);
    return language === 'es'
        ? `${sign}${groupThousands(whole, '.')}${NBSP}€`
        : `${sign}€${groupThousands(whole, ',')}`;
};
//# sourceMappingURL=money.js.map